import React, { useEffect, useRef, useState } from 'react';

const languages = [
  ['fr-FR', 'Français'], ['en-US', 'English'], ['ht-HT', 'Kreyòl ayisyen'],
  ['pt-BR', 'Português'], ['es-ES', 'Español']
];

const FloatingAssistant = ({ aiName = 'Assistant', patients = [], onSelectPatient }) => {
  const [open, setOpen] = useState(false);
  const [greeted, setGreeted] = useState(false);
  const [language, setLanguage] = useState('fr-FR');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);

  const greetingText = language === 'en-US'
    ? `Hello, I am ${aiName}, your medical assistant. I understand voice and text, and I am here to make your work easier.`
    : language === 'ht-HT'
      ? `Bonjou, mwen se ${aiName}, asistan medikal ou a. Mwen konprann vwa ak tèks, e mwen la pou fasilite travay ou.`
      : `Bonjour, je suis ${aiName}, votre assistant médical. Je comprends la voix et le texte, et je suis là pour faciliter votre travail.`;

  const speak = (text) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = language;
    window.speechSynthesis.speak(utterance);
  };

  const toggleOpen = () => {
    setOpen(prev => !prev);
    if (!greeted) {
      setAnswer(greetingText);
      speak(greetingText);
      setGreeted(true);
    }
  };

  const answerLocally = () => {
    const normalized = question.toLowerCase();
    let response = language === 'en-US' ? 'Please describe symptoms or a clinical question.' : 'Veuillez décrire des symptômes ou une question clinique.';
    if (normalized.includes('fièvre') || normalized.includes('fievre') || normalized.includes('fever')) {
      response = language === 'en-US' ? 'Check temperature, hydration and warning signs.' : 'Vérifiez la température, l’hydratation et les signes d’alerte.';
    }
    setAnswer(response);
  };

  const toggleListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('La dictée vocale n’est pas disponible dans ce navigateur.');
      return;
    }
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = language;
    recognition.continuous = false;
    recognition.onresult = (event) => setQuestion(event.results[0][0].transcript);
    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  };

  useEffect(() => () => recognitionRef.current?.stop(), []);

  return (
    <div style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 1000 }}>
      {open && (
        <div style={{ width: 'min(92vw, 340px)', marginBottom: '10px', padding: '16px', borderRadius: '14px', backgroundColor: '#fff', boxShadow: '0 10px 30px rgba(0,0,0,0.2)', border: '1px solid #dbeafe' }}>
          <strong>{aiName}</strong>
          <div style={{ marginTop: '6px' }}>
            <select value={language} onChange={(event) => setLanguage(event.target.value)} style={{ width: '100%', padding: '6px' }}>
              {languages.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
            </select>
          </div>
          <textarea value={question} onChange={(event) => setQuestion(event.target.value)} rows={2} placeholder="Écrivez ou dictez votre question" style={{ width: '100%', marginTop: '8px', padding: '8px' }} />
          <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
            <button type="button" onClick={answerLocally}>Répondre</button>
            <button type="button" onClick={toggleListening}>{listening ? 'Arrêter' : 'Dicter'}</button>
            <button type="button" onClick={() => speak(answer)} disabled={!answer}>Lire</button>
          </div>
          {answer && <p style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>{answer}</p>}
          {patients.length > 0 && onSelectPatient && (
            <div style={{ marginTop: '10px' }}>
              <small>Ouvrir un dossier patient :</small>
              <select onChange={(event) => event.target.value && onSelectPatient(patients.find(patient => String(patient.id) === event.target.value))} style={{ width: '100%', padding: '6px', marginTop: '4px' }} defaultValue="">
                <option value="">Sélectionner…</option>
                {patients.map(patient => <option key={patient.id} value={patient.id}>{patient.prenom ? `${patient.prenom} ${patient.name}` : patient.name}</option>)}
              </select>
            </div>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={toggleOpen}
        aria-label={`Assistant ${aiName}`}
        title={aiName}
        style={{ width: '56px', height: '56px', borderRadius: '50%', border: 'none', backgroundColor: '#0d3b66', color: '#fff', fontSize: '1.4rem', cursor: 'pointer', boxShadow: '0 8px 20px rgba(0,0,0,0.25)' }}
      >
        🩺
      </button>
    </div>
  );
};

export default FloatingAssistant;
